export const DEFAULT_SITE_URL = "https://panetteriaprimavera.com";
export const BRAND_NAME = "Panetteria Primavera";
export const DEFAULT_OG_IMAGE_PATH = "/og-logo.png";

export const HOME_TITLE = "Panetteria Primavera | Pizzeria e prodotti tipici salentini";
export const HOME_DESCRIPTION =
  "Menu pizzeria a Taviano e shop online con prodotti artigianali salentini: frise, taralli, olio EVO, conserve e dolci tradizionali.";

export const SHOP_TITLE = "Shop prodotti tipici salentini";
export const SHOP_DESCRIPTION =
  "Acquista online prodotti locali selezionati da Panetteria Primavera: olio EVO, conserve, taralli, frise, biscotti e dolci artigianali.";

export const LEGAL_PAGE_SEO = {
  privacy: {
    title: "Privacy Policy",
    description: "Informativa privacy di Panetteria Primavera per shop online, ordini, spedizioni e analytics."
  },
  cookie: {
    title: "Cookie Policy",
    description: "Informazioni sui cookie tecnici e analytics usati dal sito Panetteria Primavera."
  },
  terms: {
    title: "Termini di vendita",
    description: "Termini e condizioni di vendita online per i prodotti acquistati nello shop Panetteria Primavera."
  },
  shipping: {
    title: "Spedizioni e resi",
    description: "Costi di spedizione, tempi di consegna e informazioni sui resi per lo shop Panetteria Primavera."
  },
  cart: {
    title: "Carrello",
    description: "Carrello dello shop Panetteria Primavera."
  },
  success: {
    title: "Pagamento completato",
    description: "Conferma pagamento completato per un ordine Panetteria Primavera."
  },
  admin: {
    title: "Admin",
    description: "Area amministrativa Panetteria Primavera."
  }
};

export function normalizeSiteUrl(siteUrl) {
  const rawSiteUrl = String(siteUrl || DEFAULT_SITE_URL).trim();
  if (!rawSiteUrl) {
    return DEFAULT_SITE_URL;
  }

  return rawSiteUrl.replace(/\/+$/, "");
}

export function getSiteUrl() {
  return normalizeSiteUrl(process.env.REACT_APP_SITE_URL || DEFAULT_SITE_URL);
}

export function absoluteUrl(pathOrUrl, siteUrl = getSiteUrl()) {
  const value = String(pathOrUrl || "").trim();
  const normalizedSiteUrl = normalizeSiteUrl(siteUrl);

  if (!value) {
    return `${normalizedSiteUrl}/`;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return `${normalizedSiteUrl}${value.startsWith("/") ? value : `/${value}`}`;
}

export function buildPageTitle(title) {
  const cleanTitle = String(title || "").trim();
  if (!cleanTitle) {
    return HOME_TITLE;
  }

  if (cleanTitle.toLowerCase().includes(BRAND_NAME.toLowerCase())) {
    return cleanTitle;
  }

  return `${cleanTitle} | ${BRAND_NAME}`;
}

export function truncateMetaDescription(description, maxLength = 155) {
  const cleanDescription = String(description || HOME_DESCRIPTION).replace(/\s+/g, " ").trim();
  if (cleanDescription.length <= maxLength) {
    return cleanDescription;
  }

  const sliced = cleanDescription.slice(0, maxLength - 1).trimEnd();
  return `${sliced.replace(/[,.!?;:]$/, "")}…`;
}

export function formatPriceFromCents(cents) {
  const value = Math.max(0, Number(cents) || 0) / 100;
  return value.toFixed(2);
}

export function buildBreadcrumbJsonLd(items, siteUrl = getSiteUrl()) {
  const itemListElement = items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path, siteUrl)
  }));

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement
  };
}

export function buildBusinessJsonLd(siteUrl = getSiteUrl()) {
  const normalizedSiteUrl = normalizeSiteUrl(siteUrl);

  return {
    "@context": "https://schema.org",
    "@type": ["Bakery", "Restaurant"],
    "@id": `${normalizedSiteUrl}/#business`,
    name: BRAND_NAME,
    url: `${normalizedSiteUrl}/`,
    image: absoluteUrl(DEFAULT_OG_IMAGE_PATH, normalizedSiteUrl),
    logo: absoluteUrl(DEFAULT_OG_IMAGE_PATH, normalizedSiteUrl),
    telephone: "+3908331624313",
    servesCuisine: ["Pizza", "Prodotti da forno", "Cucina salentina"],
    address: {
      "@type": "PostalAddress",
      streetAddress: "Via Portofino, 20",
      postalCode: "73057",
      addressLocality: "Taviano",
      addressRegion: "LE",
      addressCountry: "IT"
    }
  };
}

export function buildProductJsonLd(product, siteUrl = getSiteUrl()) {
  const normalizedSiteUrl = normalizeSiteUrl(siteUrl);
  const slug = String(product?.slug || "").trim();
  const url = absoluteUrl(`/shop/${encodeURIComponent(slug)}`, normalizedSiteUrl);
  const imageUrl = product?.image_url ? absoluteUrl(product.image_url, normalizedSiteUrl) : absoluteUrl(DEFAULT_OG_IMAGE_PATH);

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product?.name,
    description: product?.description || `${product?.name || "Prodotto"} disponibile nello shop Panetteria Primavera.`,
    image: [imageUrl],
    sku: product?.id || `prod_${slug}`,
    brand: {
      "@type": "Brand",
      name: BRAND_NAME
    },
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: "EUR",
      price: formatPriceFromCents(product?.price_cents),
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition"
    }
  };
}
